import React, { useState, useEffect } from 'react';
import { 
  Music, Piano, Drum, Brain, Sparkles, Play, Pause,
  ExternalLink, Calendar, Zap, HeartHandshake, Layers,
  AudioWaveform, Binary, Cpu, Globe, Radio
} from 'lucide-react';

interface SoundCloudTrack {
  id: number;
  title: string;
  permalink_url: string;
  created_at: string;
  duration: number;
  playback_count: number;
  favoritings_count?: number;
  likes_count?: number;
  genre: string;
  description: string;
  artwork_url?: string;
  waveform_url?: string;
}

const MusicJourney: React.FC = () => {
  const [soundCloudData, setSoundCloudData] = useState<any>(null);
  const [tracks, setTracks] = useState<SoundCloudTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrack, setSelectedTrack] = useState<SoundCloudTrack | null>(null);
  
  // Charger les données SoundCloud
  useEffect(() => {
    // Charger les infos générales
    fetch('/soundcloud-data.json')
      .then(res => res.json())
      .then(data => setSoundCloudData(data))
      .catch(err => console.error('Erreur données générales:', err));
    
    // Charger les tracks de test
    fetch('/soundcloud-test-tracks.json')
      .then(res => res.json())
      .then(data => {
        if (data.tracks) {
          setTracks(data.tracks);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Erreur chargement tracks:', err);
        setLoading(false);
      });
  }, []);

  const totalTracks = soundCloudData?.statistics?.total_tracks || 940;

  // Formater la durée
  const formatDuration = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-purple-950 to-black">
      {/* Hero - Votre Histoire */}
      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-blue-600/10" />
        
        <div className="relative container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Titre Principal */}
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 text-center">
              Rediscovering Music
              <span className="block text-3xl md:text-4xl mt-4 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                A Fusion of Art and AI
              </span>
            </h1>

            {/* Votre Parcours */}
            <div className="prose prose-lg prose-invert mx-auto text-gray-300">
              <p className="text-xl leading-relaxed mb-6">
                After a <strong className="text-purple-400">27-year hiatus</strong>, music called me back—not for nostalgia, but for <strong className="text-blue-400">innovation</strong>. 
                The piano and drums have rekindled my improvisational talent and rhythmic passion.
              </p>
              
              <p className="text-lg leading-relaxed mb-6">
                As an <strong className="text-purple-400">AI Evangelist and Engineer</strong>, I've harnessed cutting-edge technology to enhance my creative process, 
                utilizing tools like <span className="text-orange-400">Sonic Pi</span>, VST plugins, and machine learning to bridge MIDI and text. 
              </p>

              <p className="text-lg leading-relaxed mb-8">
                This journey has culminated in <strong className="text-white text-2xl">{totalTracks}+ original songs</strong>, 
                each an instant ephemeral creation born from improvisation with piano rhythms and advanced AI algorithms.
              </p>

              {/* Vision Unique */}
              <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 rounded-2xl p-8 mb-8 border border-purple-500/30">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <Brain className="w-8 h-8 text-purple-400" />
                  Ma Vision Singulière
                </h2>
                <p className="text-gray-300">
                  Je ne consomme aucune musique mainstream et n'écoute pas de musique, 
                  forgeant un chemin de <strong className="text-purple-400">singularité curieuse</strong> qui reflète ma vision artistique unique.
                </p>
                <p className="text-gray-300 mt-4">
                  <strong className="text-blue-400">L'IA n'est pas un outil de génération</strong>, mais un partenaire pour comprendre 
                  la composition de mes fichiers MIDI et créer des articulations vocales innovantes.
                </p>
              </div>

              {/* Update Dec 2024 */}
              <div className="bg-gradient-to-r from-orange-900/50 to-red-900/50 rounded-2xl p-8 border border-orange-500/30">
                <h2 className="text-2xl font-bold text-white mb-4">
                  📅 Update Dec 2024: Transitioning to Advanced AI Research
                </h2>
                <p className="text-gray-300">
                  Après 2.5 ans de création musicale dédiée, je me concentre désormais exclusivement sur 
                  la recherche avancée en IA et la musique. Mon objectif : promouvoir l'IA pour tous, 
                  avec un accent particulier sur l'éducation via ma création, le <strong className="text-orange-400">"Genius Coach Framework"</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistiques */}
      <section className="py-16 container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <Piano className="w-10 h-10 text-purple-400 mb-3" />
            <div className="text-3xl font-bold text-white">{totalTracks}+</div>
            <div className="text-gray-400">Compositions Originales</div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <Calendar className="w-10 h-10 text-blue-400 mb-3" />
            <div className="text-3xl font-bold text-white">27 ans</div>
            <div className="text-gray-400">Pause Musicale</div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <Brain className="w-10 h-10 text-orange-400 mb-3" />
            <div className="text-3xl font-bold text-white">AI+</div>
            <div className="text-gray-400">MIDI Intelligence</div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <Sparkles className="w-10 h-10 text-yellow-400 mb-3" />
            <div className="text-3xl font-bold text-white">100%</div>
            <div className="text-gray-400">Improvisation Pure</div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-16 container mx-auto px-4">
        <h2 className="text-3xl font-bold text-white text-center mb-12">
          Stack Technologique
        </h2>
        <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
          {[
            'Sonic Pi', 'VST Plugins', 'Machine Learning', 'MIDI Processing',
            'Text-to-Music Bridge', 'Neural Networks', 'Real-time Synthesis',
            'Audio Analysis', 'Pattern Recognition', 'Algorithmic Composition'
          ].map((tech) => (
            <span
              key={tech}
              className="px-6 py-3 bg-gradient-to-r from-purple-600/20 to-blue-600/20 backdrop-blur-lg rounded-full text-gray-300 border border-purple-500/30 hover:border-purple-400 transition-colors"
            >
              {tech}
            </span>
          ))}
        </div>
      </section>

      {/* Vision Future - Label */}
      <section className="py-16 container mx-auto px-4">
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-purple-900/30 to-orange-900/30 rounded-3xl p-12 border border-purple-500/30">
          <h2 className="text-4xl font-bold text-white mb-6 text-center">
            Vision : Mon Propre Label
          </h2>
          <p className="text-xl text-gray-300 text-center mb-8">
            L'objectif final est de créer mon propre label musical, 
            affranchi des plateformes tierces et de leurs manipulations.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <Globe className="w-12 h-12 text-purple-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white">Indépendance</h3>
              <p className="text-gray-400 text-sm">Contrôle total sur ma musique</p>
            </div>
            <div className="text-center">
              <Layers className="w-12 h-12 text-blue-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white">Innovation</h3>
              <p className="text-gray-400 text-sm">Fusion AI + Créativité humaine</p>
            </div>
            <div className="text-center">
              <HeartHandshake className="w-12 h-12 text-orange-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white">Éducation</h3>
              <p className="text-gray-400 text-sm">Genius Coach Framework</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section Playlist avec tracks */}
      <section className="py-16 container mx-auto px-4">
        <h2 className="text-3xl font-bold text-white text-center mb-12">
          Collection Complète - {totalTracks} Créations
        </h2>
        
        <div className="max-w-6xl mx-auto">
          {/* Tracks récentes */}
          {tracks.length > 0 && (
            <div className="mb-12">
              <h3 className="text-2xl font-semibold text-white mb-6">
                📻 Dernières Créations
              </h3>
              <div className="space-y-4">
                {tracks.map((track) => (
                  <div
                    key={track.id}
                    className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300 hover:bg-white/10"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-white mb-2">
                          {track.title}
                        </h4>
                        <p className="text-gray-400 text-sm mb-3">
                          {track.description}
                        </p>
                        <div className="flex items-center gap-6 text-sm">
                          <span className="text-purple-400">
                            <Play className="w-4 h-4 inline mr-1" />
                            {track.playback_count.toLocaleString()} plays
                          </span>
                          <span className="text-pink-400">
                            ❤️ {track.likes_count || 0} likes
                          </span>
                          <span className="text-blue-400">
                            ⏱️ {formatDuration(track.duration)}
                          </span>
                          {track.genre && (
                            <span className="text-orange-400">
                              🎵 {track.genre}
                            </span>
                          )}
                        </div>
                      </div>
                      <a
                        href={track.permalink_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-4 p-3 bg-gradient-to-r from-orange-500/20 to-orange-600/20 rounded-lg hover:from-orange-500/30 hover:to-orange-600/30 transition-all"
                      >
                        <ExternalLink className="w-5 h-5 text-orange-400" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              
              <p className="text-center text-gray-400 mt-8">
                Affichage de {tracks.length} tracks sur {totalTracks} total
              </p>
            </div>
          )}

          {/* Lien vers SoundCloud complet */}
          <div className="text-center">
            <a
              href="https://soundcloud.com/mrdgeneva"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-full hover:from-orange-600 hover:to-orange-700 transform hover:scale-105 transition-all duration-300 shadow-2xl"
            >
              <Music className="w-6 h-6" />
              Voir toute la collection sur SoundCloud
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Wave Animation */}
      <div className="py-8">
        <div className="flex items-center justify-center gap-1">
          {Array.from({ length: 60 }, (_, i) => (
            <div
              key={i}
              className="w-1 bg-gradient-to-t from-purple-600 via-blue-500 to-orange-500 rounded-full animate-pulse opacity-80"
              style={{
                height: `${Math.sin(i * 0.2) * 50 + 80}px`,
                animationDelay: `${i * 0.03}s`,
                animationDuration: '2s'
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default MusicJourney;